import { useEffect, useMemo, useState } from "react";
import type { PortfolioBookKindV2 } from "@tradvera/contracts/portfolio-v2";
import type { MyPortfolioNextReadModel } from "../contracts.js";
import {
  getMyPortfolioNextBooks,
  getMyPortfolioNextReadModel,
  approveMyPortfolioNextBundle,
  MyPortfolioNextClientError,
  selectCurrentMyPortfolioBook,
  type MyPortfolioNextAccount,
  type MyPortfolioNextBook,
} from "../client.js";
import { statementSmartFileSummaryV2 } from "../domain/statement-smart-summary.js";
import { useStatementImportWorkspaceV2 } from "../import/useStatementImportWorkspaceV2.js";
import {
  MyPortfolioNext,
  type MyPortfolioNextProps,
  type MyPortfolioUploadViewState,
} from "./MyPortfolioNext.js";

type SelectableBookKind = Extract<PortfolioBookKindV2, "OWNED_PRIVATE" | "PAPER">;
type BookMap = Partial<Record<SelectableBookKind, MyPortfolioNextBook | null>>;
const SELECTABLE_BOOK_KINDS = ["OWNED_PRIVATE", "PAPER"] as const;

interface ResolvedBookSelection {
  books: BookMap;
  errors: Partial<Record<SelectableBookKind, string | null>>;
}

/** Keep a broken or ambiguous sibling book from disabling the selected book. */
export function resolveMyPortfolioNextBooks(available: readonly MyPortfolioNextBook[]): ResolvedBookSelection {
  const books: BookMap = {};
  const errors: ResolvedBookSelection["errors"] = {};
  for (const kind of SELECTABLE_BOOK_KINDS) {
    try {
      const book = selectCurrentMyPortfolioBook(available, kind);
      books[kind] = book;
      errors[kind] = book ? null : `NO_ACTIVE_${kind}_BOOK`;
    } catch (error) {
      books[kind] = null;
      errors[kind] = codeOf(error);
    }
  }
  return { books, errors };
}

export interface MyPortfolioNextConnectedProps {
  account?: MyPortfolioNextAccount | null;
  initialBookKind?: SelectableBookKind;
  booksEndpoint?: string;
  readEndpoint?: string;
}

export function MyPortfolioNextConnected({
  account,
  initialBookKind = "PAPER",
  booksEndpoint = "https://api.tradvera.com/v1/admin/portfolio-v2/books",
  readEndpoint,
}: MyPortfolioNextConnectedProps) {
  const [selectedKind, setSelectedKind] = useState<SelectableBookKind>(initialBookKind);
  const [books, setBooks] = useState<BookMap>({});
  const [models, setModels] = useState<MyPortfolioNextProps["models"]>({});
  const [loadingBookKind, setLoadingBookKind] = useState<SelectableBookKind | null>(null);
  const [errors, setErrors] = useState<MyPortfolioNextProps["errorByBook"]>({});
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const accountId = account?.userId ?? null;
    setModels({});
    setBooks({});
    if (!account) {
      setErrors({ PAPER: "SIGN_IN_REQUIRED", OWNED_PRIVATE: "SIGN_IN_REQUIRED" });
      setLoadingBookKind(null);
      return () => controller.abort();
    }
    setErrors({});
    setLoadingBookKind(initialBookKind);
    void (async () => {
      try {
        const list = await getMyPortfolioNextBooks({ account, booksEndpoint, signal: controller.signal });
        const resolved = resolveMyPortfolioNextBooks(list);
        const nextBooks = resolved.books;
        const nextModels: Partial<Record<SelectableBookKind, MyPortfolioNextReadModel | null>> = {};
        const nextErrors = { ...resolved.errors };
        if (!controller.signal.aborted && account.userId === accountId) {
          // Book discovery owns upload availability. Analytics loading must never revoke it.
          setBooks(nextBooks);
          setErrors(nextErrors);
        }
        await Promise.all(SELECTABLE_BOOK_KINDS.map(async (kind) => {
          const book = nextBooks[kind];
          if (!book) {
            nextModels[kind] = null;
            return;
          }
          try {
            nextModels[kind] = await getMyPortfolioNextReadModel({
              account,
              book,
              readEndpoint,
              signal: controller.signal,
            });
            nextErrors[kind] = null;
          } catch (error) {
            if (controller.signal.aborted) return;
            nextModels[kind] = null;
            nextErrors[kind] = codeOf(error);
          }
        }));
        if (!controller.signal.aborted && account.userId === accountId) {
          setBooks(nextBooks);
          setModels(nextModels);
          setErrors(nextErrors);
          setLoadingBookKind(null);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        const code = error instanceof MyPortfolioNextClientError ? error.code
          : error instanceof Error ? error.message : "MY_PORTFOLIO_NEXT_LOAD_FAILED";
        setErrors({ PAPER: code, OWNED_PRIVATE: code });
        setLoadingBookKind(null);
      }
    })();
    return () => controller.abort();
  }, [account, booksEndpoint, initialBookKind, readEndpoint, reload]);

  const common = {
    models,
    initialBookKind,
    selectedBookKind: selectedKind,
    loadingBookKind,
    errorByBook: errors,
    onBookChange: setSelectedKind,
  } satisfies MyPortfolioNextProps;
  const selectedBook = books[selectedKind] ?? null;

  if (account && selectedBook) {
    return <ImportBoundPortfolio key={`${account.userId}:${selectedBook.bookId}`}
      {...common}
      account={account}
      book={selectedBook}
      booksEndpoint={booksEndpoint}
      readEndpoint={readEndpoint}
      onCommitted={() => setReload((value) => value + 1)} />;
  }

  const upload: MyPortfolioUploadViewState = {
    connection: "UNAVAILABLE",
    busy: false,
    busyLabel: null,
    errorCode: null,
    files: [],
    canApprove: false,
    onUploadFiles: async () => ({ ok: false, errorCode: "STATEMENT_IMPORT_CONNECTION_UNAVAILABLE" }),
    onApprove: async () => false,
  };
  return <MyPortfolioNext {...common} upload={upload} />;
}

function ImportBoundPortfolio(props: MyPortfolioNextProps & {
  account: MyPortfolioNextAccount;
  book: MyPortfolioNextBook;
  booksEndpoint: string;
  readEndpoint?: string;
  onCommitted(): void;
}) {
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const storage = useMemo(() => window.localStorage, []);
  const controller = useStatementImportWorkspaceV2({
    account: props.account,
    book: props.book,
    booksEndpoint: props.booksEndpoint,
    storage,
  });
  const summaries = useMemo(() => controller.lastUploadBatch.map((imported) => {
    const summary = statementSmartFileSummaryV2(imported);
    return {
      importId: imported.importId,
      originalName: imported.file.originalName,
      role: summary.role,
      state: imported.state,
      recordCount: imported.records.length,
      actionableRecords: summary.actionableRecords,
      exceptionFields: summary.exceptionFields,
    };
  }), [controller.lastUploadBatch]);
  const busy = controller.busy !== null || approving;
  const roles = new Set(summaries.map((item) => item.role));
  const canApprove = !approved && !busy && summaries.length === 5 && roles.size === 5
    && ["POSITIONS", "CASH", "ORDER_HISTORY", "OPEN_ORDERS", "ACTIVITY_LOG"]
      .every((role) => roles.has(role as typeof summaries[number]["role"]))
    && summaries.every((item) => !["FAILED", "ABANDONED"].includes(item.state));
  const upload: MyPortfolioUploadViewState = {
    connection: "READY",
    busy,
    busyLabel: approving ? "جارٍ اعتماد الحزمة وتحديث الشاشات" : busyLabel(controller.busy),
    errorCode: approvalError ?? controller.errorCode ?? controller.capabilityError,
    files: approved ? [] : summaries,
    canApprove,
    onUploadFiles: async (files) => {
      setApproved(false);
      setApprovalError(null);
      return controller.upload(files);
    },
    onApprove: async () => {
      if (!canApprove) return false;
      setApproving(true); setApprovalError(null);
      try {
        await approveMyPortfolioNextBundle({ account: props.account, book: props.book,
          importIds: summaries.map((item) => item.importId), readEndpoint: props.readEndpoint });
        setApproved(true);
        props.onCommitted();
        return true;
      } catch (error) {
        setApprovalError(codeOf(error));
        return false;
      } finally {
        setApproving(false);
      }
    },
  };
  return <MyPortfolioNext {...props} upload={upload} />;
}

function busyLabel(value: ReturnType<typeof useStatementImportWorkspaceV2>["busy"]): string | null {
  if (value === "CAPABILITIES") return "جارٍ تجهيز قارئ الملفات";
  if (value === "HASHING_UPLOAD") return "جارٍ تشفير الأثر ورفع الملفات";
  if (value === "REVIEW") return "جارٍ تثبيت قرارات المراجعة";
  if (value === "RECONCILIATION") return "جارٍ مطابقة التنفيذات";
  if (value === "ACCEPT_INTENT" || value === "ACCEPT_CONFIRM") return "جارٍ اعتماد الملاحظات";
  if (value === "RECOVERING") return "جارٍ استعادة نتيجة العملية";
  if (value === "LOADING" || value === "DETAIL") return "جارٍ قراءة حالة الاستيراد";
  if (value === "DISCARD") return "جارٍ استبعاد الحزمة";
  return null;
}

function codeOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "STATEMENT_IMPORT_REQUEST_FAILED";
}
